import { BarChartPro, ChartsAxisData } from '@mui/x-charts-pro';
import type { BarLabelProps } from '@mui/x-charts-pro/BarChart';
import { useNavigate } from 'react-router-dom';
import { FilterableField, getSelectedTab, updateState } from '../../../store/GeneralStore';
import { ChartData } from '../../../store/Charts';
import { TextSpan } from '../../Atoms/Text';
import { truncateStr, getUTCStartAndEndMonthDates } from '../../../utils/helpers';
import { ComponentSpinner } from '../../Compounds/Loading/ComponentSpinner';

interface TallyBarChartProps {
  data: ChartData[];
  loading: boolean;
  title: string;
  filterField: string;
  filterOperator: string;
}

const SmartBarLabel = ({ x, y, width, height, children, ...other }: BarLabelProps) => {
  if (children == null) return null;

  const isSmallBar = height < 30;
  const labelY = isSmallBar ? y - 15 : y + 20;

  return (
    <text
      {...other}
      x={x + width / 2}
      fontSize={12}
      y={labelY}
      textAnchor="middle"
      dominantBaseline="middle">
      {children}
    </text>
  );
};

export const TallyBarChart = ({ data, loading, title, filterField, filterOperator }: TallyBarChartProps) => {
  const navigate = useNavigate();
  const numericValues = data.map((item: ChartData) => Number(item.value));

  const handleClick = (value?: string) => {
    updateState({
      selectedTab: {
        ...getSelectedTab(),
        assets: 2
      }
    });

    let url = '/asset';
    let filters: FilterableField[] = [];
    if (value) {
      if (filterOperator === 'between') {
        const [startDate, endDate] = getUTCStartAndEndMonthDates(value);

        filters = [
          { field: filterField, operator: 'onOrAfter', value: startDate.toISOString() },
          { field: filterField, operator: 'onOrBefore', value: endDate.toISOString() }
        ];
      } else {
        filters = [{ field: filterField, operator: filterOperator, value }];
      }
      const params = new URLSearchParams();
      params.set('filters', JSON.stringify(filters));
      url += `?${params.toString()}`;
    }
    navigate(url);
  };

  return (
    <div className="w-full">
      <div>
        <TextSpan
          className="text-center mt-3"
          size="md">
          {title}
        </TextSpan>
        <button
          className="text-xs text-indigo mb-2 ml-3"
          onClick={() => handleClick()}>
          View Assets
        </button>
      </div>
      {loading && (
        <div className="flex justify-center items-center h-[180px]">
          <ComponentSpinner />
        </div>
      )}
      {!loading && (
        <div>
          <BarChartPro
            grid={{ horizontal: true }}
            colors={['#C3D1F2']}
            dataset={data}
            height={250}
            slots={{
              barLabel: SmartBarLabel
            }}
            sx={{
              '& .MuiChartsAxisHighlight-root': {
                cursor: 'pointer',
                pointerEvents: 'all'
              }
            }}
            xAxis={[
              {
                dataKey: 'label',
                scaleType: 'band',
                valueFormatter: (value) => truncateStr(value, 15),
                tickLabelInterval: () => true,
                tickSize: 0,
                tickLabelPlacement: 'tick',
                tickPlacement: 'middle',
                tickLabelStyle: { fontSize: 10, transform: 'rotate(-90deg)', textAnchor: 'end', translate: '-8px 5px' },
                height: 100
              }
            ]}
            yAxis={[
              {
                label: 'RECORD COUNT',
                labelStyle: { fontSize: 12, transform: '', translate: '10px 0px' },
                tickSize: 0,
                tickLabelStyle: { fontSize: 12, transform: 'translate(-5px, 0px)' }
              }
            ]}
            onAxisClick={(_event: MouseEvent, data: ChartsAxisData | null) => {
              handleClick(data?.axisValue as string);
            }}
            margin={{ left: 0, right: 0, top: 20, bottom: 0 }}
            series={[
              {
                data: numericValues,
                barLabel: 'value'
              }
            ]}></BarChartPro>
        </div>
      )}
    </div>
  );
};
